import { IconBug, IconDots, IconExternalLink, IconUserShare } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, linkOptions, Outlet } from "@tanstack/react-router";
import z from "zod";
import { useDialogManager } from "@/components/dialog-manager";
import { SiteHeader } from "@/components/site-header";
import { StatusBadge } from "@/components/status-badge";
import { TabBar, type TabBarTabs } from "@/components/tab-bar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs } from "@/components/ui/tabs";
import { getEpisodeStatus } from "@/enums/episode-status";
import { getDashboardUrl, getShowUrl } from "@/lib/url";
import { orpc } from "@/orpc/client";

export const Route = createFileRoute("/_app/episodes/$episodeId")({
	component: Page,
	params: z.object({
		episodeId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.episodes.show.queryOptions({
				input: {
					episodeId: params.episodeId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { openDialog } = useDialogManager();

	const { data: episode } = useSuspenseQuery(
		orpc.episodes.show.queryOptions({
			input: {
				episodeId: params.episodeId,
			},
		}),
	);

	console.log("Channel", episode.Channel);
	console.log("Channel.Icon.ID", episode.Channel.Icon?.ID);

	const tabs: TabBarTabs = linkOptions([
		{
			key: "details",
			to: "/episodes/$episodeId",
			params: { episodeId: episode.ID },
			activeOptions: { exact: true },
			label: "Details",
		},
		{
			key: "monetization",
			to: "/episodes/$episodeId/monetization",
			params: { episodeId: episode.ID },
			label: "Monetization",
		},
		{
			key: "audio",
			to: "/episodes/$episodeId/audio",
			params: { episodeId: episode.ID },
			label: "Audio",
		},
		{
			key: "jingles",
			to: "/episodes/$episodeId/jingles",
			params: { episodeId: episode.ID },
			label: "Jingles",
		},
		{
			key: "share",
			to: "/episodes/$episodeId/share",
			params: { episodeId: episode.ID },
			label: "Share",
		},
		{
			key: "stats",
			to: "/episodes/$episodeId/stats",
			params: { episodeId: episode.ID },
			label: "Stats",
		},
		{
			key: "settings",
			to: "/episodes/$episodeId/settings",
			params: { episodeId: episode.ID },
			label: "Settings",
		},
	]);

	return (
		<>
			<SiteHeader
				breadcrumbs={[
					{
						key: "channels",
						label: "Podcasts",
						linkProps: { to: "/channels" },
					},
					{
						key: "channel",
						label: episode.Channel.Title ?? "Podcast Details",
						linkProps: {
							to: "/channels/$channelId",
							params: { channelId: episode.ChannelID },
						},
					},
					{
						key: "episodes",
						label: "Episodes",
						linkProps: {
							to: "/channels/$channelId/episodes",
							params: { channelId: episode.ChannelID },
						},
					},
					{
						key: "episode",
						label: episode.Title ?? "Episode Details",
					},
				]}
			/>
			<div className="flex flex-1 flex-col">
				<div className="@container/main flex flex-1 flex-col gap-2">
					<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
						<div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
							<div className="flex w-full flex-col justify-between gap-2 md:flex-row md:gap-4 lg:items-end">
								<div className="flex gap-3">
									<Avatar className="size-16" rounded="sm">
										<AvatarImage
											src={
												episode.Icon?.ThumbnailURL
													? `${import.meta.env.VITE_CDN_URL}${episode.Icon.ThumbnailURL}`
													: undefined
											}
										/>
										<AvatarFallback>{episode.Title?.slice(0, 1).toUpperCase()}</AvatarFallback>
									</Avatar>
									<div>
										<h1 className="text-lg leading-tight font-semibold">
											{episode.Title} <StatusBadge status={getEpisodeStatus(episode.StatusID)} />
										</h1>
										<p className="text-muted-foreground text-sm">Episode ID: #{episode.ID}</p>
									</div>
								</div>
								<div className="flex items-center justify-end gap-2">
									<ButtonGroup>
										<Button
											variant="outline"
											size="sm"
											nativeButton={false}
											render={
												<a
													href={getShowUrl(episode.Channel.UrlTitle, episode.ID.toString())}
													target="_blank"
													rel="noopener noreferrer"
												>
													<IconExternalLink />
													Open Episode
												</a>
											}
										/>
										<DropdownMenu>
											<DropdownMenuTrigger
												render={
													<Button variant="outline" size="icon-sm" aria-label="More Options">
														<IconDots />
													</Button>
												}
											/>
											<DropdownMenuContent align="end" className="min-w-40">
												<DropdownMenuGroup>
													<DropdownMenuItem
														className="whitespace-nowrap"
														render={
															<a
																href={getDashboardUrl(
																	`/podcasts/${episode.Channel.UID.toLowerCase()}/episodes/${episode.UID.toLowerCase()}`,
																)}
																target="_blank"
																rel="noopener noreferrer"
															/>
														}
													>
														<IconExternalLink />
														Open in Dashboard
													</DropdownMenuItem>
													<DropdownMenuItem
														render={
															<a
																href={getShowUrl(episode.Channel.UrlTitle, episode.UID)}
																target="_blank"
																rel="noopener noreferrer"
															/>
														}
													>
														<IconExternalLink />
														Open Show Page
													</DropdownMenuItem>
													<DropdownMenuItem
														className="whitespace-nowrap"
														onClick={() =>
															openDialog({
																type: "impersonate",
																payload: { channelId: episode.ChannelID },
															})
														}
													>
														<IconUserShare />
														Impersonate
													</DropdownMenuItem>
													<DropdownMenuItem
														onClick={() =>
															openDialog({
																type: "debug",
																payload: { title: `Episode #${episode.ID}`, data: episode },
															})
														}
													>
														<IconBug />
														Debug
													</DropdownMenuItem>
												</DropdownMenuGroup>
											</DropdownMenuContent>
										</DropdownMenu>
									</ButtonGroup>
								</div>
							</div>
						</div>
						<Tabs>
							<div className="px-4 lg:px-6">
								<TabBar tabs={tabs} />
							</div>
							<Outlet />
						</Tabs>
					</div>
				</div>
			</div>
		</>
	);
}
